import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import { useClock, useTasks } from '../lib/data'
import { CapturePalette } from './capture/CapturePalette'
import { Bell, Book, CalendarGrid, ChatBubble, Gear, GraduationCap, OpenBook, Pencil, Star, Sun, type IconProps } from './icons'
import { Sunrise } from './day/icons'

// ============================================================================
// THE SHARED SHELL — docs/design/handoff/README.md → "Shared shell".
//
// Left icon rail, 62px, white, 1px right border, 20px vertical padding, items
// centred in a column with a 14px gap. Logo 30x30. Divider 1x26.
// Nav items 36x36 radius 11, icons 18x18 stroke 1.7 round caps, each in its own
// domain hue. Settings pinned to the bottom with margin-top:auto.
//
// THE LOGO IS THE OWNER'S OWN BRAND MARK (2026-09-01), not the handoff's ink-square "L"
// monogram — an explicit owner request ("use it as the logo... instead of the L in a
// square"), and the one thing that outranks the handoff: docs/design/FIDELITY.md says the
// handoff wins when code and handoff disagree, but that rule exists to protect a design
// the owner chose, and the owner changing his own brand is the exception no fidelity rule
// can override. It ships bare — no ink chip, no rounded background, radius claim above
// dropped along with it — because "instead of the L in a square" means the square goes
// too. Source: apps/web/brand/life-os-mark-768.png, rasterized by
// scripts/generate-pwa-icons.mjs into apps/web/public/brand/rail-mark.png (120px, well
// above the 30px display footprint so it stays crisp at the app's 1.3x default zoom and
// on retina); reference that file rather than inlining the 768px source.
//
// ACTIVE STATE, read off the mockups rather than the README's one-liner. The
// README says the active item gets a `fill` background; nine of the ten .dc.html
// files render the active DOMAIN item as an `ink` tile with the icon lifted to
// its on-dark hue, and only LifeOS Connections.dc.html uses `fill` — for the
// SETTINGS item, which carries no domain hue. Both are therefore true:
//   domain item active  → ink tile, hue lifted   (bg-ink + text-<hue>-lift)
//   settings active     → fill tile, ink-2 glyph (bg-fill + text-ink-2)
//
// RAIL CONTENTS follow the orchestrator decision recorded in
// docs/design/FIDELITY.md: Chat is an 8th rail item in the `chat` hue, and
// Search is NOT a rail item — it rides the persistent header search field,
// which is why there is no magnifier below.
// ============================================================================

interface NavItem {
  to: string
  label: string
  icon: ComponentType<IconProps>
  /** Idle stroke: the domain hue. */
  hue: string
  /** Active stroke on the ink tile: the same hue lifted for a dark ground. */
  lift: string
  /**
   * THE SOURCE COLOUR THE HOVER INK IS MADE OF (motion-brief §2). A rail icon is
   * radius 11, so its mechanic is the bottom rise, and the tint that rises is
   * this hue mixed down — which is how hovering the rail answers "which system"
   * before the click. The custom property is read by `.mo-ink-rise`.
   */
  rule: string
  /**
   * Route prefix that also counts as "here". Settings is one rail item over two
   * screens (Connections and Appearance), and a rail that goes dim when you move
   * between two settings screens is telling you you left settings.
   */
  activePrefix?: string
}

const NAV: NavItem[] = [
  { to: '/', label: 'Today', icon: Sun, hue: 'text-today', lift: 'text-today-lift', rule: 'var(--color-today)' },
  // THE DAY PAGE (docs/design/handoff/day-page/, owner decision 2026-10-06): its own rail entry,
  // directly after Today; a sunrise in the Tasks hue. Today stays the journal home.
  { to: '/day', label: 'Day', icon: Sunrise, hue: 'text-tasks', lift: 'text-tasks-lift', rule: 'var(--color-tasks)' },
  { to: '/journal', label: 'Journal', icon: Book, hue: 'text-journal', lift: 'text-today-lift', rule: 'var(--color-journal)' },
  { to: '/school', label: 'School', icon: GraduationCap, hue: 'text-school', lift: 'text-school-lift', rule: 'var(--color-school)' },
  // CALENDAR — added by the calendar port. docs/design/calendar-brief.md says the rail already
  // has a calendar icon at a stated stroke; in this rail that frame and that slot are School's,
  // so this is a NEW item carrying the brief's exact stroke (the `calendar` token) and a
  // week-grid glyph rather than a second calendar frame. Flagged to the orchestrator; the
  // handoff's rail has no Calendar item to copy, which is the same gap Chat had.
  { to: '/calendar', label: 'Calendar', icon: CalendarGrid, hue: 'text-calendar', lift: 'text-calendar-lift', rule: 'var(--color-calendar)' },
  // REMINDERS, and only the WORD changed (owner, 2026-08-28). The route is still `/tasks`, the
  // API is still `/api/tasks`, the table is still `tasks`, and the hue token is still `tasks` —
  // this is a label, not a migration. The icon is the Bell the Today card's Reminders panel
  // already wears, so the two surfaces stop pointing at the same thing with different glyphs.
  { to: '/tasks', label: 'Reminders', icon: Bell, hue: 'text-tasks', lift: 'text-tasks-lift', rule: 'var(--color-tasks)' },
  { to: '/learning', label: 'Learning', icon: OpenBook, hue: 'text-learning', lift: 'text-learning-lift', rule: 'var(--color-learning)' },
  { to: '/interests', label: 'Interests', icon: Star, hue: 'text-interests', lift: 'text-interests-lift', rule: 'var(--color-interests)' },
  { to: '/chat', label: 'Chat', icon: ChatBubble, hue: 'text-chat', lift: 'text-chat-lift', rule: 'var(--color-chat)' },
  // SCRATCHPAD carries NO DOMAIN HUE, for the reason written on Settings below: a hue here is a
  // claim that a ninth system exists, and this is a holding pen rather than a system. It is the
  // one rail item whose contents never reach the server, and reading grey among eight coloured
  // glyphs is the honest version of that. Pencil rather than a new glyph: it is not in the rail
  // anywhere else, and "write something" is exactly what it means (owner, 2026-09-01).
  { to: '/scratchpad', label: 'Scratchpad', icon: Pencil, hue: 'text-ink-2', lift: 'text-ink-2', rule: 'var(--color-dash)' },
]

const SETTINGS: NavItem = {
  to: '/settings/connections',
  label: 'Settings',
  icon: Gear,
  hue: 'text-ink-2',
  lift: 'text-ink-2',
  // Settings carries no domain hue anywhere else in the shell, so its ink is the
  // neutral one. Giving it a colour here would invent a ninth domain.
  rule: 'var(--color-dash)',
  activePrefix: '/settings',
}

/**
 * The frame every screen sits in. The rail is fixed and never scrolls; the main
 * region is the screen's own business — Today deliberately does not scroll at
 * all, so the shell must not impose a scroll container on it.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const [captureOpen, setCaptureOpen] = useState(false)
  // Where focus was when ⌘K was pressed. README §Interactions: "`esc` closes and restores focus."
  const returnFocus = useRef<HTMLElement | null>(null)

  // ⌘K / Ctrl+K FROM ANYWHERE. It lives on the shell rather than on a screen because the palette
  // is an overlay ABOVE whatever route is mounted — the route never changes, which is what makes
  // "the screen behind you keeps its place" true rather than a caption.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key.toLowerCase() !== 'k' || !(e.metaKey || e.ctrlKey)) return
      e.preventDefault()
      returnFocus.current = document.activeElement as HTMLElement | null
      setCaptureOpen((was) => !was)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // FOCUS GOES BACK WHERE IT WAS, and it has to happen in an effect rather than in the close
  // handler. While the palette is open the app behind carries `inert`, and an inert element cannot
  // take focus — calling `.focus()` synchronously inside the keydown handler runs BEFORE React has
  // committed the render that removes `inert`, so focus silently falls to <body>. Effects run
  // after that commit, which is the only moment the old element is focusable again.
  useEffect(() => {
    if (captureOpen) return
    const previous = returnFocus.current
    returnFocus.current = null
    previous?.focus?.()
  }, [captureOpen])

  const closeCapture = useCallback(() => setCaptureOpen(false), [])

  return (
    <div className="flex h-full min-h-0 bg-page">
      {/* THE BLUR IS ON THE APP, NOT THE OVERLAY (README §11: the screen behind is blurred 1.5px
          under the scrim). `aria-hidden` and `inert` while the palette is open so a screen reader
          and the tab order both stop at the dialog. */}
      <div
        className={[
          'flex h-full min-h-0 flex-1',
          'transition-[filter] duration-[var(--dur-fast)] ease-[var(--ease-out)]',
          captureOpen ? 'blur-[1.5px]' : '',
        ].join(' ')}
        aria-hidden={captureOpen || undefined}
        inert={captureOpen || undefined}
      >
        <Rail />
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">{children}</div>
      </div>

      <CapturePalette open={captureOpen} onClose={closeCapture} />
    </div>
  )
}

function Rail() {
  // THE ATTENTION DOT IS A REAL STATE, NOT DECORATION. It appears on Reminders when something is
  // open and already past its date, and nowhere else — a dot that is always on is a dot nobody
  // looks at. In remote mode the tasks read throws (V6), and a failed read is not an alarm, so
  // the dot simply stays off.
  const { data: clock } = useClock()
  const { data: openTasks } = useTasks({ done: false })
  const tasksNeedAttention = Boolean(
    clock && openTasks?.some((t) => t.due !== null && t.due < clock.date),
  )

  return (
    <nav
      aria-label="Sections"
      className="flex w-[62px] flex-none flex-col items-center gap-[14px] border-r border-line bg-surface py-5"
    >
      <img src="/brand/rail-mark.png" alt="" className="size-[30px]" aria-hidden />
      <div className="h-px w-[26px] bg-line-3" aria-hidden />

      {NAV.map((item) => (
        <RailItem
          key={item.to}
          item={item}
          end={item.to === '/'}
          attention={item.to === '/tasks' && tasksNeedAttention}
        />
      ))}

      <RailItem item={SETTINGS} className="mt-auto" settings />
    </nav>
  )
}

function RailItem({
  item,
  end,
  settings,
  attention,
  className,
}: {
  item: NavItem
  end?: boolean
  settings?: boolean
  attention?: boolean
  className?: string
}) {
  const { to, label, icon: Icon, hue, lift, rule, activePrefix } = item
  const { pathname } = useLocation()
  const alsoHere = activePrefix ? pathname.startsWith(activePrefix) : false
  return (
    <NavLink
      to={to}
      end={end}
      title={label}
      style={{ '--mo-rule': rule } as CSSProperties}
      className={({ isActive: matched }) => {
        const isActive = matched || alsoHere
        return [
          'relative flex size-9 items-center justify-center rounded-row',
          // PRESS IS ON EVERY CONTROL, active or not (motion-brief §2). A rail
          // item you are already standing on still gives you the 1px.
          'mo-press',
          className ?? '',
          isActive
            ? settings
              ? `bg-fill ${hue}`
              : `bg-ink ${lift}`
            : // 12d, rail mechanic: the hue rises from the bottom edge. It
              // replaces the neutral `hover:bg-surface-4` lift that predated the
              // motion brief — a grey step said "hoverable", this says WHICH.
              `${hue} mo-ink-rise`,
        ].join(' ')
      }}
    >
      <Icon size={18} strokeWidth={1.7} />
      <span className="sr-only">
        {label}
        {attention && ' — something is overdue'}
      </span>
      {attention && (
        <span
          // 7px danger-dot at top-right with a 1.5px white ring.
          className="absolute right-[3px] top-[4px] size-[7px] rounded-full border-[1.5px] border-surface bg-danger-dot"
          aria-hidden
        />
      )}
    </NavLink>
  )
}
